import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { addMonths, format, startOfDay } from "date-fns";
import type { Database } from "@nfp-tracker/utils";
import { captureException, trackEvent } from "../integrations/analytics/client";
import { supabase } from "../integrations/supabase/client";
import { deriveCyclesFromObservations } from "../utils/derivedCycles";

export type ObservationRow = Database["public"]["Tables"]["observations"]["Row"];

// ~12 cycles of history, plus slack, in one shot. Routine navigation (switching days,
// browsing recent months, the dashboard's charts) stays inside this window and never
// triggers another fetch. Pages that need the full unbounded history (CSV/PDF export,
// the all-cycles history list) fetch directly instead — those are infrequent, explicit
// actions, not the day-to-day navigation this cache is for.
const CACHE_WINDOW_MONTHS = 13;

export const observationsQueryKey = (ownerUserId: string | undefined) =>
  ["observations", ownerUserId] as const;

/** Upsert-by-id, keeping the array sorted. Shared by the realtime sync and by saveDraft's
 * immediate post-save cache update, so both merge changed rows the same way. */
export const mergeObservationRow = (
  prev: ObservationRow[] | undefined,
  row: ObservationRow
): ObservationRow[] | undefined => {
  if (!prev) return prev;

  const existingIndex = prev.findIndex((r) => r.id === row.id);
  if (existingIndex === -1) {
    return [...prev, row].sort((a, b) => a.observation_date.localeCompare(b.observation_date));
  }

  const next = [...prev];
  next[existingIndex] = row;
  return next;
};

export const removeObservationRow = (
  prev: ObservationRow[] | undefined,
  id: string
): ObservationRow[] | undefined => {
  if (!prev) return prev;
  return prev.filter((row) => row.id !== id);
};

const fetchObservationsWindow = async (ownerUserId: string): Promise<ObservationRow[]> => {
  const windowStart = format(addMonths(startOfDay(new Date()), -CACHE_WINDOW_MONTHS), "yyyy-MM-dd");

  const { data, error } = await supabase
    .from("observations")
    .select("*")
    .eq("owner_user_id", ownerUserId)
    .gte("observation_date", windowStart)
    .order("observation_date", { ascending: true });

  if (error) throw error;
  return data || [];
};

/**
 * Shared observations + derived-cycles cache. One fetch per owner per app session;
 * `useObservationsRealtimeSync` (mounted once in AppShellLayout) keeps it fresh by merging
 * changed rows into this cache in place, so pages reading from here never need to refetch
 * just because something changed elsewhere in the app.
 */
export const useObservationsCache = (
  ownerUserId: string | undefined,
  methods: string[] = ["Marquette"]
) => {
  const query = useQuery({
    queryKey: observationsQueryKey(ownerUserId),
    queryFn: () => fetchObservationsWindow(ownerUserId as string),
    enabled: Boolean(ownerUserId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!query.error) return;
    trackEvent("observations_load_failed", {});
    captureException(query.error, { source: "observations_cache" });
  }, [query.error]);

  const rawObservations = query.data ?? [];
  const derived = useMemo(
    () => deriveCyclesFromObservations(rawObservations, methods),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rawObservations, methods.join(",")]
  );

  return {
    rawObservations,
    observations: derived.observations,
    cycles: derived.cycles,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};
